import { computed } from "vue";
import { defineStore } from "pinia";
import { useSearchIdsStore } from "@/store/common/search_id_store";
import { LocationServices } from "@/service/common/location_service";
import type { CountryData, RegionData, Terminal } from "@/interface/common/location_inteface";

export const useImportLocationStore = defineStore("useImportLocationStore", {
  state: (): {
    locations: RegionData[];
    filteredLocations: RegionData[];
    terminalSelected: Terminal;
    countrySelected: CountryData;
    regionSelected: RegionData;
    selectedCountry: string;
    selectedRegion: string;
    selectedTerminal: string;
    isLoading: boolean;
    error: string | null;
    tempLocation: Terminal;
    anayticsTerminalDetails: {
      region: string;
      country: string;
      terminalName: string;
      oneStreamCode: string;
      tabName: string;
    };
  } => ({
    locations: [] as RegionData[],
    filteredLocations: [] as RegionData[],
    terminalSelected: {} as Terminal,
    regionSelected: {} as RegionData,
    countrySelected: {} as CountryData,
    selectedCountry: "All",
    selectedRegion: "All",
    selectedTerminal: null,
    isLoading: false,
    error: null,
    tempLocation: {} as Terminal,
    anayticsTerminalDetails: {
      region: "",
      country: "",
      terminalName: "",
      oneStreamCode: "",
      tabName: "Import Availability"
    }
  }),
  persist: false,
  getters: {
    getImportSelectedTerminal(): Terminal {
      return this.terminalSelected;
    },
    getImportSelectedCountry(): CountryData {
      return this.countrySelected;
    }
  },
  actions: {
    async fetchImportLocations(): Promise<void> {
      this.isLoading = true;
      this.error = null;
      try {
        const searchIdsStore = useSearchIdsStore();
        const transactionType = computed((): string => searchIdsStore.transactionType);
        const filterKey: string = transactionType.value.toLowerCase();
        const service = new LocationServices();
        const importAssets = await service.fetchLocations();
        this.locations = importAssets;
        const filteredData: RegionData[] = importAssets
          .map((region) => ({
            ...region,
            countries: region.countries
              .map((country) => ({
                ...country,
                terminals: country.terminals.filter((terminal): boolean => terminal.modules.includes(filterKey))
              }))
              .filter((country): boolean => country.terminals.length > 0)
          }))
          .filter((region): boolean => region.countries.length > 0);
        this.filteredLocations = filteredData;
      } catch (e: any) {
        this.isLoading = false;
        this.error = e.message || "Unknown error occurred";
      }
    },

    // Change the drop down value for region and country based on terminal selected
    changeRegionCountry(terminal: Terminal): void {
      this.fetchRegion(terminal);
      this.fetchCountry(terminal);
    },

    // Fetch region based on terminal selected
    fetchRegion(terminal: Terminal): RegionData {
      this.terminalSelected = terminal;
      const region = this.locations.find((region): any =>
        region?.countries?.find((country): any =>
          country?.terminals?.find((term): boolean => term?.name === terminal?.name)
        )
      );
      this.regionSelected = region as RegionData;
      return this.regionSelected;
    },

    // Fetch country based on terminal selected
    fetchCountry(terminal: Terminal): CountryData {
      this.terminalSelected = terminal;
      const country = this.locations.find((region): any =>
        region?.countries?.find((country): any =>
          country?.terminals?.find((term): boolean => term?.name === terminal?.name)
        )
      );
      this.countrySelected = country?.countries[0] as CountryData;
      return this.countrySelected;
    },

    // Set terminal data
    setTerminal(terminal: any): void {
      this.terminalSelected = terminal;
      this.changeRegionCountry(terminal);
    },

    // Set terminal data
    setCountry(terminal: Terminal): void {
      this.terminalSelected = terminal;
    },

    // Set temp locatoion
    setTempLocation(location: any): void {
      this.tempLocation = location;
    },

    updateAnayticsTerminalDetails(val) {
      this.anayticsTerminalDetails = val;
    }
  }
});
